<template>
  <div class="relative" ref="menuRef">
    <!-- Avatar Button -->
    <button
      @click="toggleMenu"
      class="relative w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm transition-all hover:ring-2 hover:ring-green-600 hover:ring-offset-2 cursor-pointer"
      style="background-color: #EDD97A;"
      aria-label="User menu"
    >
      {{ userInitials }}
    </button>

    <!-- Dropdown Menu -->
    <Transition name="dropdown">
      <div
        v-if="isMenuOpen"
        class="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
      >
        <!-- User Info -->
        <div class="px-4 py-3 border-b border-gray-200">
          <p class="text-sm font-semibold text-gray-900 truncate">{{ userStore.userName }}</p>
          <p class="text-xs text-gray-500 truncate">{{ userStore.userEmail }}</p>
        </div>

        <!-- Menu Items -->
        <div class="py-1">
          <button
            @click="goToProfile"
            class="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3"
          >
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span>Profile</span>
          </button>

          <button
            @click="goToOrders"
            class="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3"
          >
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span>My Orders</span>
          </button>

          <button
            @click="goToTerms"
            class="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3"
          >
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Terms and Conditions</span>
          </button>

          <!-- Admin Panel (solo si es admin) -->
          <div v-if="userStore.isAdmin" class="border-t border-gray-200 my-1">
            <button
              @click="goToAdmin"
              class="w-full px-4 py-2.5 text-left text-sm font-semibold transition-colors cursor-pointer flex items-center gap-3"
              style="color: #EDD97A;"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Admin Panel</span>
            </button>
          </div>

          <!-- Logout -->
          <div class="border-t border-gray-200 mt-1">
            <button
              @click="handleLogout"
              class="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-3"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Toast Notification -->
    <Toast
      v-if="showLogoutToast"
      type="logout"
      title="Logged out"
      message="You have been successfully logged out"
      :duration="4000"
      @close="showLogoutToast = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import Toast from '@/components/ui/Toast.vue'

const router = useRouter()
const userStore = useUserStore()

const isMenuOpen = ref(false)
const menuRef = ref<HTMLElement | null>(null)
const showLogoutToast = ref(false)

const userInitials = computed(() => userStore.userInitials)

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

const goToProfile = () => {
  closeMenu()
  router.push('/profile')
}

const goToOrders = () => {
  closeMenu()
  router.push('/orders')
}

const goToTerms = () => {
  closeMenu()
  router.push('/terms')
}

const goToAdmin = () => {
  closeMenu()
  router.push('/admin/dashboard')
}

const handleLogout = () => {
  closeMenu()
  
  // Mostrar toast de logout primero
  showLogoutToast.value = true
  
  // Esperar suficiente tiempo para que el usuario vea el toast completo
  // El toast dura 4000ms, esperamos 3500ms antes de hacer logout
  setTimeout(() => {
    // Hacer logout
    userStore.logout()
    
    // Esperar un poco más para que se complete la animación de salida del toast
    setTimeout(() => {
      // Redirigir después de que el toast haya desaparecido
      showLogoutToast.value = false
      setTimeout(() => {
        router.push('/')
      }, 300)
    }, 500)
  }, 3500)
}

// Cerrar menú al hacer clic fuera
const handleClickOutside = (event: MouseEvent) => {
  if (menuRef.value && !menuRef.value.contains(event.target as Node)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Dropdown Transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

